import { Textarea } from "../componentes/Textarea"
import { NativeSelect } from "../componentes/NativeSelect"
import { cn } from "../lib/cn"
import { Input } from "../componentes/Input"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  api,
  type ConferenciaDoMes,
  type ContratoSobFiscalizacao,
  type DetalheDaFiscalizacao,
  type ItemDeConferenciaNaTela,
  type MedicaoNaTela,
  type OcorrenciaNaTela,
} from "../api"
import { data, inteiro, moeda, moedaCurta, numeroComAno, plural } from "../formato"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../componentes/Collapsible"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import { Paginacao, usePaginacaoNaTela } from "../componentes/Paginacao"
import * as Icones from "../componentes/Icones"
import { Empty, EmptyContent, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { FalhaNaSecao } from "../componentes/Secao"
import { Skeleton } from "../componentes/Skeleton"
import { TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { Numero, Numeros } from "../componentes/Numeros"
import { Selo, chaveConhecida, type ChaveDeSituacao } from "../componentes/Selo"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../componentes/Table"
import { RotuloDoCampo } from "../componentes/RotuloDoCampo"
import { BOTAO_CANCELAR, CORPO_LISTRADO, TEXTO_DO_CADASTRO } from "../lib/classes"
import { SUCESSO } from "../ficha/Tabela"
import { GRAVIDADES_DA_OCORRENCIA, GRAVIDADE_INICIAL } from "../lib/gravidades"

export const LISTRADO_NO_CARTAO = `${CORPO_LISTRADO} [:root:not([data-tema=escuro])_&>tr:nth-child(even):not([data-slot=linha-expandida]):not([data-listra=nao])]:bg-(--azul-50) [:root:not([data-tema=escuro])_&>tr:nth-child(n):not([data-slot=linha-expandida]):not([data-listra=nao]):hover]:bg-(--realce-forte) [[data-tema=escuro]_&>tr:nth-child(even):not([data-slot=linha-expandida]):not([data-listra=nao])]:bg-(--azul-900) [[data-tema=escuro]_&>tr:nth-child(n):not([data-slot=linha-expandida]):not([data-listra=nao]):hover]:bg-(--linha-sob-o-cursor)`

type Props = {
  aoAbrirContrato: (id: number) => void
}

const NOME_DA_SITUACAO: Record<string, string> = {
  aberta: "aberta",
  apurada: "apurada",
  atestada: "atestada",
  rejeitada: "rejeitada",
}

const NOME_DO_PAPEL: Record<string, string> = {
  gestor: "gestor",
  fiscal_titular: "fiscal titular",
  fiscal_substituto: "fiscal substituto",
}

export function manterOrdem<T extends { contrato_id: number }>(anteriores: T[], novos: T[]): T[] {
  const porId = new Map(novos.map((item) => [item.contrato_id, item]))
  const mantidos = anteriores.flatMap((item) => {
    const novo = porId.get(item.contrato_id)
    return novo === undefined ? [] : [novo]
  })
  const vistos = new Set(anteriores.map((item) => item.contrato_id))
  return [...mantidos, ...novos.filter((item) => !vistos.has(item.contrato_id))]
}

function semAcento(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
}

function TituloDaSecao({ children }: { children: string }) {
  return <h3 className="m-0 text-xs font-semibold tracking-wider text-muted-foreground uppercase">{children}</h3>
}

function SeloSemIcone({ situacao, children }: { situacao: ChaveDeSituacao; children: string }) {
  return (
    <Selo situacao={situacao} semIcone classe="py-1">
      {children}
    </Selo>
  )
}

function PrazoDoContrato({ dias }: { dias: number | null }) {
  if (dias === null) return <span className="text-xs text-muted-foreground">Vigência não lida</span>
  if (dias < 0) return <SeloSemIcone situacao="critico">{`Vencido há ${plural(-dias, "dia", "dias")}`}</SeloSemIcone>
  if (dias <= 15) return <SeloSemIcone situacao="atencao">{dias === 0 ? "Vence hoje" : `Vence em ${plural(dias, "dia", "dias")}`}</SeloSemIcone>
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icones.Vigente />
      {`Vence em ${plural(dias, "dia", "dias")}`}
    </span>
  )
}

function EsqueletoDaLista() {
  return (
    <Card aria-busy="true">
      <CardContent className="flex flex-col gap-3">
        <Skeleton className="h-4 w-48" />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(10.5rem,100%),1fr))] gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </CardContent>
    </Card>
  )
}

function EsqueletoDoDetalhe() {
  return (
    <div aria-busy="true" className="flex flex-col gap-2">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  )
}

export function Fiscalizacao({ aoAbrirContrato }: Props) {
  const [itens, definirItens] = useState<ContratoSobFiscalizacao[] | null>(null)
  const [meta, definirMeta] = useState<Record<string, number> | null>(null)
  const [erro, definirErro] = useState<string | null>(null)
  const [aberto, definirAberto] = useState<number | null>(null)
  const [detalhe, definirDetalhe] = useState<DetalheDaFiscalizacao | null>(null)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [aviso, definirAviso] = useState<string | null>(null)
  const abertoAgora = useRef<number | null>(null)
  abertoAgora.current = aberto
  const gatilhos = useRef(new Map<number, HTMLButtonElement>())
  const [busca, definirBusca] = useState("")

  const carregar = useCallback(() => {
    definirErro(null)
    api
      .fiscalizacao()
      .then((resposta) => {
        definirItens((anteriores) =>
          abertoAgora.current !== null && anteriores !== null ? manterOrdem(anteriores, resposta.dados) : resposta.dados,
        )
        definirMeta(resposta.meta)
      })
      .catch((falha: unknown) => {
        definirErro(falha instanceof Error ? falha.message : "falha ao carregar")
      })
  }, [])

  useEffect(carregar, [carregar])

  const abrir = (contratoId: number) => {
    if (aberto === contratoId) {
      definirAberto(null)
      definirAviso(null)
      return
    }
    definirAberto(contratoId)
    definirDetalhe(null)
    definirRecusa(null)
    definirAviso(null)
    api
      .fiscalizacaoDoContrato(contratoId)
      .then(definirDetalhe)
      .catch((falha: unknown) => {
        definirRecusa(falha instanceof Error ? falha.message : "falha ao carregar")
      })
  }

  if (erro !== null) {
    return (
      <Card>
        <CardContent>
          <FalhaNaSecao mensagem={`Não foi possível carregar a fiscalização: ${erro}`} aoTentar={carregar} />
        </CardContent>
      </Card>
    )
  }

  if (itens === null) {
    return <EsqueletoDaLista />
  }

  const termo = semAcento(busca.trim())
  const visiveis =
    termo === "" ? itens : itens.filter((item) => semAcento(`${numeroComAno(item.numero, item.ano)} ${item.objeto}`).includes(termo))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Minha fiscalização</CardTitle>
        {itens.length > 0 && (
          <CardAction className="w-full md:w-[364px]">
            <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Número ou objeto" />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Numeros>
          <Numero rotulo="Contratos sob sua responsabilidade" valor={inteiro(meta?.contratos ?? 0)} />
          <Numero rotulo="Medições aguardando você" valor={inteiro(meta?.medicoes_aguardando ?? 0)} />
          <Numero rotulo="Conferências do mês pendentes" valor={inteiro(meta?.conferencia_pendente ?? 0)} />
          <Numero rotulo="Ocorrências registradas" valor={inteiro(meta?.ocorrencias_registradas ?? 0)} />
        </Numeros>
        {itens.length === 0 ? (
          <p className="py-6 text-center text-sm leading-relaxed text-muted-foreground">
            Você não tem designação vigente em nenhum contrato desta entidade. A designação é ato formal — portaria ou
            ordem de serviço — e é ela que autoriza atestar e registrar ocorrência (art. 117 da Lei 14.133/2021).
          </p>
        ) : visiveis.length === 0 ? (
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icones.Buscar />
              </EmptyMedia>
              <EmptyTitle>Nenhum contrato com esta pesquisa</EmptyTitle>
            </EmptyHeader>
            <EmptyContent>
              <Button type="button" variant="outline" size="sm" onClick={() => definirBusca("")}>
                <Icones.Fechar />
                Limpar pesquisa
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="flex flex-col gap-3">
            {visiveis.map((item) => {
              const estaAberto = aberto === item.contrato_id
              const Seta = estaAberto ? Icones.Diminuir : Icones.Aumentar
              return (
                <Collapsible key={item.contrato_id} asChild open={estaAberto} onOpenChange={() => abrir(item.contrato_id)}>
                  <div
                    data-slot="cartao-do-contrato"
                    className={cn("rounded-md border bg-card transition-colors", estaAberto ? "border-primary" : "border-border")}
                  >
                    <div className="flex cursor-pointer flex-col gap-2 p-4" onClick={() => abrir(item.contrato_id)}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              ref={(elemento) => {
                                if (elemento) gatilhos.current.set(item.contrato_id, elemento)
                                else gatilhos.current.delete(item.contrato_id)
                              }}
                              className="cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {numeroComAno(item.numero, item.ano)}
                            </button>
                          </CollapsibleTrigger>
                          <Selo situacao="papel" semIcone>{NOME_DO_PAPEL[item.papel] ?? item.papel}</Selo>
                          {item.medicoes_aguardando > 0 && (
                            <Selo situacao="atencao">
                              {`${inteiro(item.medicoes_aguardando)} ${item.medicoes_aguardando === 1 ? "medição" : "medições"} a atestar · ${moedaCurta(item.valor_aguardando)}`}
                            </Selo>
                          )}
                          {item.conferencia_pendente > 0 && (
                            <Selo situacao="contagem">{`${inteiro(item.conferencia_pendente)} a conferir`}</Selo>
                          )}
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                          <PrazoDoContrato dias={item.dias_para_vencer} />
                          <span data-slot="seta" data-aberto={estaAberto} aria-hidden="true" className="flex text-muted-foreground">
                            <Seta tamanho={16} />
                          </span>
                        </div>
                      </div>
                      <p className={cn("m-0 text-xs text-foreground", TEXTO_DO_CADASTRO)}>{item.objeto}</p>
                    </div>

                    <CollapsibleContent>
                      {estaAberto && (
                        <div className="cursor-default rounded-b-md border-t border-border bg-background p-4" onClick={(e) => e.stopPropagation()}>
                          <DetalheDoContrato
                            contratoId={item.contrato_id}
                            detalhe={detalhe}
                            recusa={recusa}
                            aviso={aviso}
                            aoMudar={(avisoDoAto) => {
                              definirAviso(avisoDoAto ?? null)
                              carregar()
                              api.fiscalizacaoDoContrato(item.contrato_id).then((novo) => {
                                definirDetalhe(novo)
                                requestAnimationFrame(() => {
                                  const gatilho = gatilhos.current.get(item.contrato_id)
                                  gatilho?.focus({ preventScroll: true })
                                  gatilho?.scrollIntoView({ block: "nearest" })
                                })
                              })
                            }}
                            aoRecusar={definirRecusa}
                            aoAbrirFicha={() => aoAbrirContrato(item.contrato_id)}
                          />
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const LINHA_EMPILHADA = "max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-x-2 max-md:py-1.5"
const CELULA_SOLTA = "max-md:w-auto max-md:border-0"

const CABECA_DA_TABELA = "h-9 px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase"

const GRAVIDADE_NO_SELO: Record<string, ChaveDeSituacao> = {
  grave: "critico",
  media: "atencao",
  leve: "informacao",
  informacao: "informacao",
}

function Ocorrencia({ ocorrencia }: { ocorrencia: OcorrenciaNaTela }) {
  const rotulo = GRAVIDADES_DA_OCORRENCIA.find((g) => g.valor === ocorrencia.gravidade)?.rotulo ?? ocorrencia.gravidade
  return (
    <li className="flex flex-col gap-1 rounded-md border border-border bg-card p-3 text-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="font-semibold text-foreground">{ocorrencia.titulo}</span>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-muted-foreground tabular-nums">{data(ocorrencia.data)}</span>
          <SeloSemIcone situacao={GRAVIDADE_NO_SELO[ocorrencia.gravidade] ?? "informacao"}>{rotulo}</SeloSemIcone>
        </span>
      </div>
      {ocorrencia.descricao !== "" && <p className="m-0 text-muted-foreground">{ocorrencia.descricao}</p>}
      {ocorrencia.providencia !== null && (
        <p className="m-0 text-muted-foreground">
          <strong className="font-semibold text-foreground">Providência:</strong> {ocorrencia.providencia}
        </p>
      )}
    </li>
  )
}

function DetalheDoContrato({
  contratoId,
  detalhe,
  recusa,
  aviso,
  aoMudar,
  aoRecusar,
  aoAbrirFicha,
}: {
  contratoId: number
  detalhe: DetalheDaFiscalizacao | null
  recusa: string | null
  aviso: string | null
  aoMudar: (aviso?: string) => void
  aoRecusar: (mensagem: string) => void
  aoAbrirFicha: () => void
}) {
  const { visiveis: visiveisMedicoes, paginacao: paginacaoMedicoes } = usePaginacaoNaTela(detalhe?.medicoes ?? [], 10)
  const { visiveis: visiveisOcorrencias, paginacao: paginacaoOcorrencias } = usePaginacaoNaTela(detalhe?.ocorrencias ?? [], 10)
  if (recusa !== null) {
    return <p className="rounded-md bg-muted p-3 text-sm font-medium text-destructive">{recusa}</p>
  }
  if (detalhe === null) {
    return <EsqueletoDoDetalhe />
  }

  return (
    <div className="flex flex-col gap-6">
      {aviso !== null && (
        <p role="status" className="rounded-md bg-status-sucesso px-3 py-2 text-sm font-medium text-sobre-status-sucesso">
          {aviso}
        </p>
      )}
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TituloDaSecao>Medições do contrato</TituloDaSecao>
          <Button variant="outline" size="sm" onClick={aoAbrirFicha}>
            <Icones.Abrir />
            Abrir a ficha do contrato
          </Button>
        </div>
        {detalhe.medicoes.length === 0 ? (
          <p className="m-0 text-xs text-muted-foreground">Nenhuma medição registrada neste contrato.</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-sm border bg-card">
              <Table className="border-collapse text-xs leading-(--linha-densa) max-md:block [&_td]:px-3 [&_td]:py-2 max-md:[&_tbody]:block">
                <TableHeader className="bg-muted max-md:hidden">
                  <TableRow>
                    <TableHead className={cn(CABECA_DA_TABELA, "w-[1%]")}>Nº</TableHead>
                    <TableHead className={CABECA_DA_TABELA}>Competência</TableHead>
                    <TableHead className={cn(CABECA_DA_TABELA, "w-[1%] text-right")}>Valor</TableHead>
                    <TableHead className={cn(CABECA_DA_TABELA, "w-[1%]")}>Situação</TableHead>
                    <TableHead className={cn(CABECA_DA_TABELA, "w-[1%] text-right")}>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className={LISTRADO_NO_CARTAO}>
                  {visiveisMedicoes.map((medicao) => (
                    <LinhaDaMedicao key={medicao.id} medicao={medicao} aoMudar={aoMudar} />
                  ))}
                </TableBody>
              </Table>
            </div>
            <Paginacao {...paginacaoMedicoes} />
          </>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <TituloDaSecao>Ocorrências do contrato</TituloDaSecao>
        {detalhe.ocorrencias.length === 0 ? (
          <p className="m-0 text-xs text-muted-foreground">Nenhuma ocorrência registrada.</p>
        ) : (
          <>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {visiveisOcorrencias.map((ocorrencia) => (
                <Ocorrencia key={ocorrencia.id} ocorrencia={ocorrencia} />
              ))}
            </ul>
            <Paginacao {...paginacaoOcorrencias} />
          </>
        )}
        <NovaOcorrencia contratoId={contratoId} aoRegistrar={() => aoMudar("Ocorrência registrada.")} />
      </section>

      <Conferencia contratoId={contratoId} aoMudar={aoMudar} aoRecusar={aoRecusar} />
    </div>
  )
}

export function LinhaDaMedicao({
  medicao,
  aoMudar,
}: {
  medicao: MedicaoNaTela
  aoMudar: (aviso?: string) => void
}) {
  const [motivo, definirMotivo] = useState("")
  const [pedindoMotivo, definirPedindoMotivo] = useState(false)
  const [confirmando, definirConfirmando] = useState<boolean | null>(null)
  const botaoDaDecisao = useRef<HTMLButtonElement | null>(null)

  const confirmar = (aceita: boolean, botao: HTMLButtonElement) => {
    botaoDaDecisao.current = botao
    definirConfirmando(aceita)
  }

  const gravar = (aceita: boolean, senha: string) =>
    api.atestar(medicao.id, aceita, motivo.trim() === "" ? null : motivo.trim(), senha).then(() => {
      definirPedindoMotivo(false)
      definirMotivo("")
      aoMudar(`Medição nº ${medicao.numero} ${aceita ? "atestada" : "rejeitada"}.`)
    })

  const decidida = medicao.situacao === "atestada" || medicao.situacao === "rejeitada"
  const semMotivo = motivo.trim() === ""

  return (
    <>
      <TableRow className={LINHA_EMPILHADA}>
        <TableCell className={cn("w-[1%] font-medium whitespace-nowrap max-md:order-1", CELULA_SOLTA)}>nº {medicao.numero}</TableCell>
        <TableCell className={cn("whitespace-normal text-muted-foreground max-md:order-3 max-md:basis-full", CELULA_SOLTA)}>
          <span className="whitespace-nowrap">
            {data(medicao.competencia_inicio)} a {data(medicao.competencia_fim)}
          </span>
          {medicao.ressalva !== null && (
            <p className="m-0 mt-1 text-[0.6875rem] text-muted-foreground">
              <strong className="font-semibold text-foreground">Ressalva:</strong> {medicao.ressalva}
            </p>
          )}
        </TableCell>
        <TableCell className={cn("w-[1%] text-right font-medium whitespace-nowrap tabular-nums max-md:order-4", CELULA_SOLTA)}>
          {moeda(medicao.valor)}
        </TableCell>
        <TableCell className={cn("w-[1%] max-md:order-2 max-md:ml-auto", CELULA_SOLTA)}>
          <Selo situacao={chaveConhecida(medicao.situacao, "contagem")} semIcone>
            {NOME_DA_SITUACAO[medicao.situacao] ?? medicao.situacao}
          </Selo>
        </TableCell>
        <TableCell className={cn("w-[1%] text-right tabular-nums max-md:order-5 max-md:ml-auto", CELULA_SOLTA)}>
          {decidida ? (
            <span className="text-muted-foreground">{medicao.atestada_em !== null ? data(medicao.atestada_em) : "—"}</span>
          ) : (
            <div className="flex items-center justify-end gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className={SUCESSO}
                disabled={confirmando !== null}
                onClick={(evento) => confirmar(true, evento.currentTarget)}
              >
                <Icones.Validar />
                Atestar
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-expanded={pedindoMotivo}
                disabled={confirmando !== null}
                onClick={() => definirPedindoMotivo(!pedindoMotivo)}
                className="border border-destructive text-destructive hover:text-destructive"
              >
                <Icones.Recusada />
                Rejeitar
              </Button>
            </div>
          )}
          <ConfirmarComSenha
            aberto={confirmando !== null}
            titulo={`${confirmando === false ? "Rejeitar" : "Atestar"} a medição nº ${medicao.numero}`}
            tom={confirmando === false ? "perigo" : "azul"}
            resumo={
              <ResumoDoAto
                itens={[
                  ["Medição", `nº ${medicao.numero} · ${data(medicao.competencia_inicio)} a ${data(medicao.competencia_fim)}`],
                  ["Valor", moeda(medicao.valor)],
                  ...(confirmando === false ? [["Motivo", motivo.trim()] as [string, string]] : []),
                ]}
              />
            }
            rotuloDoAto={confirmando === false ? "Rejeitar" : "Atestar"}
            iconeDoAto={confirmando === false ? <Icones.Recusada /> : <Icones.Validar />}
            aoConfirmar={(senha) => gravar(confirmando !== false, senha)}
            aoFechar={() => definirConfirmando(null)}
            aoDevolverFoco={() => {
              if (botaoDaDecisao.current?.isConnected) botaoDaDecisao.current.focus()
            }}
          />
        </TableCell>
      </TableRow>
      {pedindoMotivo && (
        <TableRow data-slot="linha-do-motivo" data-listra="nao" className={cn("bg-card hover:bg-card", LINHA_EMPILHADA)}>
          <TableCell colSpan={5} className={cn("whitespace-normal max-md:basis-full", CELULA_SOLTA)}>
            <div className="flex max-w-2xl flex-col gap-2 py-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 [&>[data-slot=rotulo-do-campo]]:mb-0">
                <RotuloDoCampo htmlFor={`motivo-${medicao.id}`} obrigatorio>
                  Motivo da rejeição
                </RotuloDoCampo>
                <Input
                  id={`motivo-${medicao.id}`}
                  required
                  value={motivo}
                  onChange={(evento) => definirMotivo(evento.target.value)}
                  placeholder="Diga por que a medição está sendo rejeitada."
                  className="h-8 min-w-48 flex-1 text-xs"
                />
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={BOTAO_CANCELAR}
                  onClick={() => {
                    definirPedindoMotivo(false)
                    definirMotivo("")
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  title={semMotivo ? "Falta o motivo" : undefined}
                  disabled={semMotivo || confirmando !== null}
                  onClick={(evento) => confirmar(false, evento.currentTarget)}
                >
                  Continuar com a rejeição
                </Button>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function NovaOcorrencia({
  contratoId,
  aoRegistrar,
}: {
  contratoId: number
  aoRegistrar: () => void
}) {
  const [abrindo, definirAbrindo] = useState(false)
  const [titulo, definirTitulo] = useState("")
  const [descricao, definirDescricao] = useState("")
  const [gravidade, definirGravidade] = useState<string>(GRAVIDADE_INICIAL)
  const [providencia, definirProvidencia] = useState("")
  const [confirmando, definirConfirmando] = useState(false)

  if (!abrindo) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="w-full border-dashed bg-card hover:bg-azul-200"
        onClick={() => definirAbrindo(true)}
      >
        <Icones.Mais />
        Registrar ocorrência
      </Button>
    )
  }

  const gravar = (senha: string) =>
    api
      .registrarOcorrencia(
        contratoId,
        {
          titulo,
          descricao,
          gravidade,
          providencia: providencia.trim() === "" ? null : providencia.trim(),
        },
        senha,
      )
      .then(() => {
        definirAbrindo(false)
        definirTitulo("")
        definirDescricao("")
        definirProvidencia("")
        aoRegistrar()
      })
  const rotuloDaGravidade = GRAVIDADES_DA_OCORRENCIA.find((g) => g.valor === gravidade)?.rotulo ?? gravidade

  return (
    <div className="flex flex-col gap-3 p-4 border border-border rounded-lg bg-card">
      <p className="m-0 text-sm font-semibold text-foreground">Nova ocorrência</p>
      <div className="flex flex-col">
        <RotuloDoCampo htmlFor="ocorrencia-titulo" obrigatorio>
          Título resumido
        </RotuloDoCampo>
        <Input
          id="ocorrencia-titulo"
          required
          value={titulo}
          onChange={(evento) => definirTitulo(evento.target.value)}
          className="h-8 text-xs"
          placeholder="Título resumido da ocorrência"
        />
      </div>

      <div className="flex flex-col">
        <RotuloDoCampo htmlFor="ocorrencia-descricao">Descrição</RotuloDoCampo>
        <Textarea
          id="ocorrencia-descricao"
          className="min-h-0 text-xs md:text-xs"
          rows={3}
          value={descricao}
          onChange={(evento) => definirDescricao(evento.target.value)}
          placeholder="Detalhes adicionais do fato..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex flex-col">
          <RotuloDoCampo htmlFor="ocorrencia-gravidade" obrigatorio>
            Gravidade
          </RotuloDoCampo>
          <NativeSelect
            id="ocorrencia-gravidade"
            required
            className="h-8 text-xs"
            value={gravidade}
            onChange={(evento) => definirGravidade(evento.target.value)}
          >
            {GRAVIDADES_DA_OCORRENCIA.map((g) => (
              <option key={g.valor} value={g.valor}>
                {g.rotulo}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col">
          <RotuloDoCampo htmlFor="ocorrencia-providencia">Providência tomada</RotuloDoCampo>
          <Input
            className="h-8 text-xs"
            id="ocorrencia-providencia"
            value={providencia}
            onChange={(evento) => definirProvidencia(evento.target.value)}
            placeholder="Opcional"
          />
        </div>
      </div>

      <p className="text-[0.6875rem] text-muted-foreground italic">
        A gravidade ordena a leitura de quem for decidir. Ela não bloqueia
        pagamento nem aplica sanção por si.
      </p>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="border-destructive text-destructive hover:text-destructive"
          onClick={() => definirAbrindo(false)}
        >
          Cancelar
        </Button>
        <Button
          size="sm"
          title={titulo.trim() === "" ? "Falta o título" : undefined}
          disabled={confirmando || titulo.trim() === ""}
          onClick={() => definirConfirmando(true)}
        >
          <Icones.Enviar />
          Registrar
        </Button>
      </div>
      <ConfirmarComSenha
        aberto={confirmando}
        titulo="Registrar a ocorrência"
        resumo={
          <ResumoDoAto
            itens={[
              ["Título", titulo.trim()],
              ["Gravidade", rotuloDaGravidade],
              ...(providencia.trim() === "" ? [] : [["Providência", providencia.trim()] as [string, string]]),
            ]}
          />
        }
        rotuloDoAto="Registrar"
        iconeDoAto={<Icones.Enviar />}
        aoConfirmar={gravar}
        aoFechar={() => definirConfirmando(false)}
      />
    </div>
  )
}

const SITUACOES_DO_ITEM = [
  { valor: "pendente", rotulo: "Pendente" },
  { valor: "conferido", rotulo: "Conferido" },
  { valor: "com_ressalva", rotulo: "Com ressalva" },
  { valor: "nao_aplicavel", rotulo: "Não se aplica" },
]

const CHAVE_DO_ITEM: Record<string, ChaveDeSituacao> = {
  conferido: "conferido",
  com_ressalva: "com_ressalva",
  nao_aplicavel: "nao_aplicavel",
  pendente: "pendente_de_conferencia",
}

const NOME_DO_ITEM: Record<string, string> = {
  conferido: "conferido",
  com_ressalva: "com ressalva",
  nao_aplicavel: "não se aplica",
  pendente: "pendente",
}

function Conferencia({
  contratoId,
  aoMudar,
  aoRecusar,
}: {
  contratoId: number
  aoMudar: (aviso?: string) => void
  aoRecusar: (mensagem: string) => void
}) {
  const [mes, definirMes] = useState<ConferenciaDoMes | null>(null)

  const carregar = useCallback(() => {
    api
      .conferencia(contratoId)
      .then(definirMes)
      .catch((falha: unknown) => {
        aoRecusar(falha instanceof Error ? falha.message : "falha ao carregar")
      })
  }, [contratoId, aoRecusar])

  useEffect(carregar, [carregar])

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TituloDaSecao>Conferência do mês</TituloDaSecao>
        {mes !== null && (
          <Selo situacao={mes.pendentes > 0 ? "pendente_de_conferencia" : "conferido"} semIcone>
            {mes.pendentes > 0 ? `${inteiro(mes.pendentes)} a conferir` : "tudo conferido"}
          </Selo>
        )}
      </div>
      {mes === null ? (
        <EsqueletoDoDetalhe />
      ) : (
        <div className="overflow-x-auto rounded-sm border bg-card">
          <Table className="border-collapse text-xs leading-(--linha-densa) max-md:block [&_td]:px-3 [&_td]:py-2 max-md:[&_tbody]:block">
            <TableBody className={LISTRADO_NO_CARTAO}>
              {mes.itens.map((item) => (
                <LinhaDoItem
                  key={item.id}
                  item={item}
                  aoMarcar={() => {
                    carregar()
                    aoMudar("Conferência do mês salva.")
                  }}
                  aoRecusar={aoRecusar}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

export function LinhaDoItem({
  item,
  aoMarcar,
  aoRecusar,
}: {
  item: ItemDeConferenciaNaTela
  aoMarcar: () => void
  aoRecusar: (mensagem: string) => void
}) {
  const [observacao, definirObservacao] = useState(item.observacao ?? "")
  const [pedindoMotivo, definirPedindoMotivo] = useState<string | null>(null)
  const [mostrandoFundamento, definirMostrandoFundamento] = useState(false)

  const marcar = (situacao: string) => {
    const precisaDeMotivo = situacao === "com_ressalva" || situacao === "nao_aplicavel"
    if (precisaDeMotivo && observacao.trim() === "") {
      definirPedindoMotivo(situacao)
      return
    }
    api
      .marcarConferencia(item.id, situacao, observacao.trim() === "" ? null : observacao.trim())
      .then(() => {
        definirPedindoMotivo(null)
        aoMarcar()
      })
      .catch((falha: unknown) => {
        aoRecusar(falha instanceof Error ? falha.message : "não foi possível salvar")
      })
  }

  const temFundamento = item.fundamento !== null

  return (
    <TableRow
      className={cn(LINHA_EMPILHADA, temFundamento && "cursor-pointer")}
      onClick={temFundamento ? () => definirMostrandoFundamento(!mostrandoFundamento) : undefined}
    >
      <TableCell className={cn("w-[1%]", CELULA_SOLTA)}>
        <Selo situacao={CHAVE_DO_ITEM[item.situacao] ?? "contagem"} semIcone>
          {NOME_DO_ITEM[item.situacao] ?? item.situacao}
        </Selo>
      </TableCell>
      <TableCell className={cn("whitespace-normal text-muted-foreground max-md:order-last max-md:basis-full", CELULA_SOLTA)}>
        <span className="font-medium text-foreground">{item.rotulo}</span>
        {item.observacao !== null && <div className="mt-0.5 text-[0.6875rem] text-muted-foreground">{item.observacao}</div>}
        {item.fundamento !== null && (
          <Collapsible open={mostrandoFundamento} onOpenChange={definirMostrandoFundamento}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-[0.6875rem] font-medium text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <Icones.Fundamento />
                {mostrandoFundamento ? "Esconder o fundamento" : "Fundamento legal"}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-1 rounded border border-border bg-muted p-2 text-[0.6875rem] text-muted-foreground">
                {item.fundamento}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
        {pedindoMotivo !== null && (
          <div className="mt-2 flex flex-col border-t border-border pt-2" onClick={(e) => e.stopPropagation()}>
            <RotuloDoCampo htmlFor={`obs-${item.id}`} obrigatorio>
              {pedindoMotivo === "com_ressalva" ? "O que foi ressalvado" : "Por que não se aplica a este contrato"}
            </RotuloDoCampo>
            <Input
              id={`obs-${item.id}`}
              required
              value={observacao}
              onChange={(evento) => definirObservacao(evento.target.value)}
              className="h-8 text-xs"
            />
            <div className="mt-1.5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={BOTAO_CANCELAR}
                onClick={() => definirPedindoMotivo(null)}
              >
                <Icones.Fechar />
                Cancelar
              </Button>
              <Button
                size="sm"
                className="bg-status-sucesso text-sobre-status-sucesso hover:bg-status-sucesso-pressionado"
                onClick={() => marcar(pedindoMotivo)}
              >
                <Icones.Salvar />
                Salvar
              </Button>
            </div>
          </div>
        )}
      </TableCell>
      <TableCell className={cn("w-[1%] text-right max-md:ml-auto", CELULA_SOLTA)} onClick={(e) => e.stopPropagation()}>
        <NativeSelect
          aria-label={`Situação de: ${item.rotulo}`}
          className="h-8 w-40 text-xs max-md:w-32"
          value={pedindoMotivo ?? item.situacao}
          onChange={(evento) => marcar(evento.target.value)}
        >
          {SITUACOES_DO_ITEM.map((s) => (
            <option key={s.valor} value={s.valor}>
              {s.rotulo}
            </option>
          ))}
        </NativeSelect>
      </TableCell>
    </TableRow>
  )
}
